import { MouseEvent, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Avatar, Box, Button, Card, Typography, useTheme } from '@mui/material';

import { StateFilter } from '@/lib/filter/stakingPoolState';
import { toast } from 'react-toastify';
import SymbolSvg from '@/assets/assetfi/Symbol';
import { addCommas } from '@/lib/util';
import { PoolState, WITHDRAWALLPRINCIPAL } from '@/lib/common/name-space';
import { useState } from 'react';
import { useWeb3 } from '@/hooks/use-web3';
import BigNumber from 'bignumber.js';
import { isCompleteModalType } from '@/types';
import CommonModal from '../modal/CommonModal';
import Loading from '@/component/mui/Backdrop';
import { setCurrencyTicker } from '@/component/admin/currency';

interface PortfolioCardPropsType {
  name: string;
  description?: string;
  stakeAmount: string;
  APR: string;
  duration?: string | null;
  state: string;
  contractAddr?: string | undefined;
  address: string;
}

const PortfolioCard = ({
  name,
  description,
  stakeAmount,
  APR,
  duration,
  state,
  contractAddr,
  address,
}: PortfolioCardPropsType) => {
  // theme
  const theme = useTheme();

  // router
  const navigate = useNavigate();
  if (!contractAddr) {
    toast.error('Contract address does not exist.');
    navigate('/portfolio');
  }

  if (BigNumber(stakeAmount).lte('0')) {
    return;
  }

  return (
    <>
      <Card
        onClick={(e: MouseEvent<HTMLDivElement>) => {
          e.stopPropagation();
          const target = e.target as HTMLElement;
          if (target.tagName === 'BUTTON' || target.closest('button')) {
            return;
          }
          navigate('/portfolioDetail', {
            state: {
              contractAddr,
            },
          });
        }}
        sx={{
          flex: 1,
          cursor: 'pointer',
          display: { xs: 'flex', md: 'none' },
          flexDirection: 'column',
          gap: '24px',
          padding: '24px',
          borderRadius: '20px',
          border: `0.5px solid ${theme.greyscale.lightgrey1}`,
          boxShadow: '0px 0px 4px 0px #00000026',
        }}
      >
        <Box sx={{ display: 'flex', gap: '10px' }}>
          <SymbolSvg width={24} height={24} />
          <Typography variant="h6" color={theme.greyscale.darkgrey2}>
            {name}
          </Typography>
        </Box>

        <Box>
          <Typography variant="body2" color={theme.greyscale.grey1}>
            Stake Amount
          </Typography>
          <Typography component={'div'} variant="h4" color={theme.greyscale.darkgrey2}>
            {addCommas(stakeAmount)}{' '}
            <Typography display={'inline'} variant="h6" color={theme.greyscale.darkgrey2}>
              SFL
            </Typography>
          </Typography>
        </Box>

        <Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              borderTop: `0.5px solid ${theme.greyscale.lightgrey1}`,
              paddingTop: '8px',
              paddingBottom: '8px',
            }}
          >
            <Typography variant="body2" color={theme.greyscale.grey1}>
              APR
            </Typography>

            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="subtitle2" color={theme.greyscale.darkgrey2}>
                {APR} %
              </Typography>
            </Box>
          </Box>
          {/* <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              borderTop: `0.5px solid ${theme.greyscale.lightgrey1}`,
              paddingTop: '8px',
              paddingBottom: '8px',
            }}
          >
            <Typography variant="body2" color={theme.greyscale.grey1}>
              Duration
            </Typography>

            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="subtitle2" color={theme.greyscale.darkgrey2}>
                123123
              </Typography>
            </Box>
          </Box> */}
          {/* <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              borderTop: `0.5px solid ${theme.greyscale.lightgrey1}`,
              paddingTop: '8px',
              paddingBottom: '8px',
            }}
          >
            <Typography variant="body2" color={theme.greyscale.grey1}>
              Total Rewards
            </Typography>

            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="subtitle2" color={theme.greyscale.darkgrey2}>
                {totalRewards.toLocaleString()} USD
              </Typography>
            </Box>
          </Box> */}

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              borderTop: `0.5px solid ${theme.greyscale.lightgrey1}`,
              paddingTop: '8px',
            }}
          >
            <Typography variant="body2" color={theme.greyscale.grey1}>
              Withdraw
            </Typography>

            <Box sx={{ textAlign: 'right' }}>
              <Typography
                variant="subtitle2"
                color={state === 'open' ? theme.background.blue : theme.greyscale.darkgrey2}
              >
                <Withdraw state={state} contractAddr={contractAddr} address={address} />
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              borderTop: `0.5px solid ${theme.greyscale.lightgrey1}`,
              paddingTop: '8px',
            }}
          >
            <Typography variant="body2" color={theme.greyscale.grey1}>
              Pool Status
            </Typography>

            <Box sx={{ textAlign: 'right' }}>
              <Typography
                variant="subtitle2"
                color={state === 'open' ? theme.background.blue : theme.greyscale.darkgrey2}
              >
                {StateFilter(state)}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Card>

      <Card
        onClick={(e: MouseEvent<HTMLDivElement>) => {
          e.stopPropagation();
          const target = e.target as HTMLElement;
          if (target.tagName === 'BUTTON' || target.closest('button')) {
            return;
          }
          navigate('/portfolioDetail', {
            state: {
              contractAddr,
            },
          });
        }}
        // onClick={() => navigate(`/portfolio/${id}`)}
        sx={{
          flex: 1,
          cursor: 'pointer',
          display: { xs: 'none', md: 'flex' },
          justifyContent: 'space-between',
          padding: '12px 20px',
          borderRadius: '10px',
          border: `0.5px solid ${theme.greyscale.lightgrey1}`,
          boxShadow: '0px 0px 4px 0px #00000026',
          background: state === 'open' ? '#ffffff' : theme.background.default,
        }}
      >
        <Box sx={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
          <SymbolSvg width={24} height={24} />
          <Box>
            <Typography variant="body3" color={theme.greyscale.grey1}>
              Pool Name
            </Typography>
            <Typography variant="h6" color={theme.greyscale.darkgrey2}>
              {name}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: '20px' }}>
          <Box>
            <Typography variant="body3" color={theme.greyscale.grey1}>
              Stake Amount
            </Typography>
            <Typography variant="subtitle1" color={theme.greyscale.darkgrey2}>
              {addCommas(stakeAmount)}{' '}
              <Typography display={'inline'} variant="body3" color={theme.greyscale.darkgrey2}>
                SFL
              </Typography>
            </Typography>
          </Box>

          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body3" color={theme.greyscale.grey1}>
              APR
            </Typography>
            <Typography variant="subtitle1" color={theme.greyscale.darkgrey2}>
              {APR} %
            </Typography>
          </Box>

          {/* <Box>
            <Typography variant="body3" color={theme.greyscale.grey1}>
              Duration
            </Typography>
            <Typography variant="subtitle1" color={theme.greyscale.darkgrey2}>
              {duration}
            </Typography>
          </Box> */}

          {/* <Box>
            <Typography variant="body3" color={theme.greyscale.grey1}>
              Total Rewards
            </Typography>
            <Typography variant="subtitle1" color={theme.greyscale.darkgrey2}>
              {totalRewards.toLocaleString()}{' '}
              <Typography display={'inline'} variant="body3" color={theme.greyscale.darkgrey2}>
                USD
              </Typography>
            </Typography>
          </Box> */}
        </Box>

        <Box sx={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6" color={theme.greyscale.darkgrey2}>
              <Withdraw state={state} contractAddr={contractAddr} address={address} />
            </Typography>
          </Box>
        </Box>

        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="body3" color={theme.greyscale.grey1}>
            Pool Status
          </Typography>
          <Typography
            variant="body1"
            color={
              state === PoolState.POOLSTATE_WAITING ||
              state === PoolState.POOLSTATE_FUNDRAISING ||
              state === PoolState.POOLSTATE_OPERATING
                ? theme.background.blue
                : theme.greyscale.darkgrey2
            }
          >
            {StateFilter(state)}
          </Typography>
        </Box>
      </Card>
    </>
  );
};

export default PortfolioCard;

function Withdraw({
  state,
  contractAddr,
  address,
}: {
  state: string;
  contractAddr: string | undefined;
  address: string;
}) {
  const [isCompleteModal, setIsCompleteModal] = useState<isCompleteModalType>({
    success: false,
    fail: false,
  });

  const theme = useTheme();
  const { web3authCall } = useWeb3();
  const [isLoading, setIsLoading] = useState(false);
  const txHash = useRef('');

  const triggerIsCompleteModal = (key: keyof isCompleteModalType) => {
    setIsCompleteModal((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  if (
    state === PoolState.POOLSTATE_FUNDRAISING_STOPPED ||
    state === PoolState.POOLSTATE_FUNDRAISING_FAILED ||
    state === PoolState.POOLSTATE_OPERATING_STOPPED ||
    state === PoolState.POOLSTATE_OPERATING_CLOSED
  ) {
    return (
      <>
        <Box sx={{ textAlign: 'center' }}>
          <Button
            variant="contained"
            onClick={async (e) => {
              e.stopPropagation();
              console.log(web3authCall?.connected());
              if (web3authCall?.connected() && contractAddr && address) {
                setIsLoading(true);
                const adminCode = await setCurrencyTicker(web3authCall);
                if (adminCode.code !== 200) {
                  setIsLoading(false);
                  toast.error('Update failed');
                  return;
                }
                const re = await web3authCall?.callStaking(WITHDRAWALLPRINCIPAL, contractAddr, [address]);
                setIsLoading(false);
                if (re.code === 200) {
                  triggerIsCompleteModal('success');
                } else {
                  triggerIsCompleteModal('fail');
                }
                txHash.current = re.data.txHash;
              }
            }}
          >
            Withdraw
          </Button>
        </Box>

        <CommonModal
          open={isCompleteModal.success}
          handleClose={() => {
            triggerIsCompleteModal('success');
          }}
          title="Claim Complete"
          type="success"
        >
          <Typography color={theme.greyscale.grey2} variant="h6">
            Withdraw Completed Successfully.
            <br />
            Thank You.
            <Button>goToHash</Button>
          </Typography>
        </CommonModal>

        {/*  Claim 실패 시 */}
        <CommonModal
          open={isCompleteModal.fail}
          handleClose={() => {
            triggerIsCompleteModal('fail');
          }}
          title="Error"
          type="error"
        >
          <Typography color={theme.greyscale.grey2} variant="h6">
            Withdraw Failed.
          </Typography>
        </CommonModal>
        <Loading isLoading={isLoading} />
      </>
    );
  } else {
    return <></>;
  }
}
